import type { ServerPlayers } from "@/api";

const snapshotKeys = new Set([
  "serverId",
  "capturedAt",
  "players",
  "playersOnline",
  "playersMax",
  "stale",
]);
const playerKeys = new Set(["name", "uuid", "latencyMs"]);

/** Validate the standalone player-session snapshot before accepting any transport. */
export function isPlayerSnapshot(value: unknown): value is ServerPlayers {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const snapshot = value as Record<string, unknown>;
  if (
    Object.keys(snapshot).some((key) => !snapshotKeys.has(key)) ||
    typeof snapshot.serverId !== "string" ||
    !snapshot.serverId ||
    typeof snapshot.stale !== "boolean" ||
    !(
      snapshot.capturedAt === null ||
      (typeof snapshot.capturedAt === "string" && Number.isFinite(Date.parse(snapshot.capturedAt)))
    ) ||
    !Array.isArray(snapshot.players) ||
    snapshot.players.length > 20_000 ||
    !Number.isSafeInteger(snapshot.playersOnline) ||
    snapshot.playersOnline !== snapshot.players.length ||
    !(
      snapshot.playersMax === null ||
      (Number.isSafeInteger(snapshot.playersMax) && Number(snapshot.playersMax) >= 0)
    )
  ) {
    return false;
  }
  const names = new Set<string>();
  return snapshot.players.every((player: unknown) => {
    if (typeof player !== "object" || player === null || Array.isArray(player)) {
      return false;
    }
    const entry = player as Record<string, unknown>;
    if (
      Object.keys(entry).some((key) => !playerKeys.has(key)) ||
      typeof entry.name !== "string" ||
      Array.from(entry.name).length < 1 ||
      Array.from(entry.name).length > 16 ||
      Array.from(entry.name).some(
        (char) => char.codePointAt(0)! <= 31 || char.codePointAt(0) === 127,
      ) ||
      (entry.uuid !== undefined &&
        (typeof entry.uuid !== "string" || !entry.uuid || entry.uuid.length > 128)) ||
      (entry.latencyMs !== undefined &&
        (typeof entry.latencyMs !== "number" ||
          !Number.isFinite(entry.latencyMs) ||
          entry.latencyMs < 0))
    ) {
      return false;
    }
    const key = entry.name.toLowerCase();
    if (names.has(key)) {
      return false;
    }
    names.add(key);
    return true;
  });
}

/** REST, subscription and live events share one source-time fence per displayed server. */
export class PlayerSnapshotFence {
  private latest: number | null = null;
  constructor(private readonly serverId: string) {}

  accept(value: unknown): value is ServerPlayers {
    if (!isPlayerSnapshot(value) || value.serverId !== this.serverId) {
      return false;
    }
    if (value.capturedAt === null) {
      return this.latest === null && value.stale;
    }
    const captured = Date.parse(value.capturedAt);
    if (captured > Date.now()) {
      return false;
    }
    if (this.latest !== null && captured <= this.latest) {
      return false;
    }
    this.latest = captured;
    return true;
  }
}
